import { useCallback, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';

declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          prompt: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

interface GoogleCredentialResponse {
  credential: string;
  select_by: string;
}

interface GoogleUserInfo {
  email: string;
  given_name: string;
  family_name: string;
  sub: string;
  picture?: string;
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

export const useGoogleSignIn = () => {
  const { googleSignIn, isLoading } = useAuthStore();

  const decodeJWT = (token: string): GoogleUserInfo | null => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode JWT:', error);
      return null;
    }
  };

  const handleCredentialResponse = useCallback(
    async (response: GoogleCredentialResponse) => {
      try {
        const userInfo = decodeJWT(response.credential);
        if (!userInfo) {
          throw new Error('Failed to decode Google credential');
        }

        await googleSignIn({
          email: userInfo.email,
          firstName: userInfo.given_name,
          lastName: userInfo.family_name,
          googleId: userInfo.sub,
          avatar: userInfo.picture,
        });
      } catch (error) {
        console.error('Google Sign-In error:', error);
      }
    },
    [googleSignIn]
  );

  const initializeGoogleSignIn = useCallback(() => {
    if (!window.google || !GOOGLE_CLIENT_ID) {
      console.warn('Google Sign-In not available or client ID not configured');
      return;
    }

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleCredentialResponse,
      auto_select: false,
      cancel_on_tap_outside: true,
    });
  }, [handleCredentialResponse]);

  const renderGoogleButton = useCallback(
    (element: HTMLElement, options?: { theme?: 'outline' | 'filled_blue' | 'filled_black'; size?: 'large' | 'medium' | 'small'; text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin' }) => {
      if (!window.google || !element) return;

      window.google.accounts.id.renderButton(element, {
        theme: options?.theme || 'outline',
        size: options?.size || 'large',
        text: options?.text || 'continue_with',
        width: '100%',
      });
    },
    []
  );

  const signInWithGoogle = useCallback(() => {
    if (!window.google) return;
    window.google.accounts.id.prompt();
  }, []);

  useEffect(() => {
    // Initialize when Google library is loaded
    const checkGoogleLoaded = () => {
      if (window.google) {
        initializeGoogleSignIn();
      } else {
        // Retry after a short delay
        setTimeout(checkGoogleLoaded, 100);
      }
    };

    checkGoogleLoaded();
  }, [initializeGoogleSignIn]);

  return {
    renderGoogleButton,
    signInWithGoogle,
    isLoading,
    isGoogleLoaded: !!window.google,
  };
}; 